import { useLazyQuery } from "@apollo/client/react";
import CloseIcon from '@mui/icons-material/Close';
import { Button, Grid, IconButton } from "@mui/material";
import { Fragment, useEffect, useState } from "react";
import { CustomDialog } from "../../../../../../components/customDialog/CustomDialog";
import DataGridGenerico from "../../../../../../components/dataGrid/DataGridGenerico";
import { generateColumnsDefaultConfiguration } from "../../../../../../components/dataGrid/columnHelpers";
import { RenderInputByTypeMobile } from "../../../../../../components/formGroupGenerator/RenderInputByTypeMobile";
import { useVisita } from "../../../../../../context/VisitaContext";
import { GET_CONTRATOS_VISITA, parseContratosVisita } from "../../../../../../data/graphQL/queries/MisVisitas/getContratosByVisita";
import { GET_INFORMACION_GENERAL, parseInformacionGeneral } from "../../../../../../data/graphQL/queries/MisVisitas/getInfoGeneral";
import CardBox from "../../CardBox/CardBox";
import InfoRow from "../../InfoRow/InfoRow";

const constratosColumns = [
    { field: 'idEvaluacion', headerName: '#', width: 90 },
    { field: 'nombrePlan', headerName: 'Plan', width: 150 },
    { field: 'periodo', headerName: 'Fecha', width: 150 }];


const ID_TIPO_PROFESIONAL_MEDICO = 1;

// Config pura, fuera del componente: los inputs propios de cada variante.
const INPUTS_CONFIG = {
    general: [
        { name: 'idPaciente', displayName: 'HC', type: 'infoRow' },
        { name: 'paciente', displayName: 'Paciente', type: 'infoRow' },
        { name: 'fechaNacimiento', displayName: 'Fecha Nac', type: 'infoRow' },
        { name: 'edad', displayName: 'Edad', type: 'infoRow' },
        { name: 'direccion', displayName: 'Dirección', type: 'infoRow' },
        { name: 'observacionAsistencial', displayName: 'Obs. Asistencial', type: 'infoRow' },
        { name: 'telefono', displayName: 'Teléfono', type: 'infoRow' },
        { name: 'telefonFamiliar', displayName: 'Teléfono Familiar', type: 'infoRow' },
        { name: 'idEvaluacion', displayName: 'Contrato', type: 'custom' },
        { name: 'periodo', displayName: 'Periodo', type: 'infoRow' },
        { name: 'programa', displayName: 'Programa', type: 'infoRow' },
        { name: 'planAsistencial', displayName: 'Plan Asistencial', type: 'infoRow' },
        { name: 'cliente', displayName: 'Financiador', type: 'infoRow' },
        { name: 'frecuencia', displayName: 'Frecuencia de Visita', type: 'infoRow' },
        { name: 'visitas', displayName: 'Visitas', type: 'infoRow' }],

    contratos: [
        { name: 'idEvaluacion', displayName: 'Contrato', type: 'custom' },
        { name: 'periodo', displayName: 'Periodo', type: 'infoRow' },
        { name: 'periodoSuspension', displayName: 'Periodo Suspensión', type: 'infoRow' },
        { name: 'programa', displayName: 'Programa', type: 'infoRow' },
        { name: 'planAsistencial', displayName: 'Plan Asistencial', type: 'infoRow' },
        { name: 'cliente', displayName: 'Financiador', type: 'infoRow' },
        { name: 'frecuencia', displayName: 'Frecuencia de Visita', type: 'infoRow' },
        { name: 'visitas', displayName: 'Visitas', type: 'infoRow' },
        { name: 'directivasAnticipadas', displayName: 'Directivas Anticipadas', type: 'string', disabled: true }]

};

export default function InformacionVisita({
    variant,
    visita,
    setVisita,
    idPaciente,
    idEvaluacion,
    idProfesional,
    idFrecuencia,
    programa,
    idTipoProfesional,
    visitasProgramadas,
    periodo
}) {
    // idVisita solo se usa en la variante "contratos", pero el hook se llama siempre
    // porque las reglas de hooks no permiten llamarlo condicionalmente.
    const { visita: { idVisita } } = useVisita();

    const esProgramaDeCuidados = 'CP' === programa;
    const esMedico = idTipoProfesional === ID_TIPO_PROFESIONAL_MEDICO;

    const [openModal, setOpenModal] = useState(false);
    const [contratosVisita, setContratosVisita] = useState([]);

    const [getContratosVisita, { data: dataContratosVisita }] = useLazyQuery(GET_CONTRATOS_VISITA, { skip: !idPaciente });

    useEffect(() => {
        if (!dataContratosVisita) return;
        const parsedData = dataContratosVisita?.evaluacionesByIdPaciente ? parseContratosVisita(dataContratosVisita.evaluacionesByIdPaciente) : [];
        setContratosVisita(parsedData);
    }, [dataContratosVisita]);

    const [getInformacionGeneral, { data: dataInformacionGeneral }] = useLazyQuery(GET_INFORMACION_GENERAL, {
        skip: !idEvaluacion || !idProfesional
    });

    useEffect(() => {
        if (!dataInformacionGeneral) return;
        if (dataInformacionGeneral?.informacionGeneralVisita?.success) {
            setVisita((prev) => ({
                ...prev,
                ...parseInformacionGeneral(dataInformacionGeneral.informacionGeneralVisita),
                visitas: visitasProgramadas,
                periodo: periodo
            }));
        }
    }, [dataInformacionGeneral]);

    useEffect(() => {
        if (!idPaciente) return;
        getContratosVisita({ variables: { idPaciente } });
    }, [idPaciente]);

    // Dos efectos separados (uno por variante) en vez de uno con dependencias dinámicas:
    // cada uno se auto-desactiva si no es su variante, y así las dependencias quedan
    // estáticas y fieles al comportamiento original de cada componente.
    useEffect(() => {
        if (variant !== 'general') return;
        if (!idEvaluacion || !idProfesional) return;

        getInformacionGeneral({ variables: { idEvaluacion, idProfesional } });
    }, [variant, idEvaluacion, idPaciente]);

    useEffect(() => {
        if (variant !== 'contratos') return;
        if (!idEvaluacion || !idProfesional) return;

        const idFrecuenciaValor = idFrecuencia || 0;
        getInformacionGeneral({ variables: { idEvaluacion, idProfesional, idFrecuencia: idFrecuenciaValor, idVisita } });
    }, [variant, idEvaluacion, idPaciente, idVisita, idFrecuencia]);

    const handleOpenModal = () => {
        setOpenModal(true);
    };
    const handleCloseModal = () => {
        setOpenModal(false);
    };

    const configuratedColumns = generateColumnsDefaultConfiguration(constratosColumns);

    const inputs = INPUTS_CONFIG[variant];

    return (
        <CardBox>
            {inputs.map((input) => {
                if (variant === 'general') {
                    return (
                        <Fragment key={input.name}>
                            {input.name === 'contrato' &&
                                <InfoRow sxLabel={{ display: 'flex', alignItems: 'center' }} label={<>
                                    {input.displayName}
                                    <Button
                                        size="small"
                                        onClick={handleOpenModal}
                                        sx={{
                                            marginLeft: 0.5,
                                            textTransform: 'none',
                                            fontWeight: 600
                                        }}>
                                        Ver más</Button>
                                </>} value={visita[input.name]} />
                            }
                            {input.name !== 'contrato' &&
                                <RenderInputByTypeMobile
                                    input={input}
                                    value={visita[input.name]} />
                            }
                        </Fragment>);

                }

                return (
                    <Fragment key={input.name}>
                        {input.name === 'idEvaluacion' &&
                            <InfoRow sxLabel={{ display: 'flex', alignItems: 'center' }} label={<>
                                {input.displayName}
                                {/* <Button
                     size="small"
                     onClick={handleOpenModal}
                     sx={{
                         marginLeft: 0.5,
                         textTransform: 'none',
                         fontWeight: 600
                     }}
                  >Ver más</Button> */}
                            </>} value={visita[input.name]} />
                        }
                        {input.name === 'periodoSuspension' && !!visita?.periodoSuspension &&
                            <Grid
                                size={{ xs: 12, sm: 6, md: 6, lg: 4 }}
                                sx={{ borderRadius: '5px', bgcolor: 'rgba(255, 0, 0, 0.286)', padding: '5px' }}>

                                <InfoRow
                                    label={input.displayName}
                                    value={visita[input.name]} />

                            </Grid>
                        }
                        {input.name === 'directivasAnticipadas' && esMedico && esProgramaDeCuidados &&
                            <RenderInputByTypeMobile
                                input={input}
                                value={visita[input.name]} />
                        }
                        {!['directivasAnticipadas', 'idEvaluacion', 'periodoSuspension'].includes(input.name) &&
                            <RenderInputByTypeMobile
                                input={input}
                                value={visita[input.name]} />
                        }
                    </Fragment>);

            })}
            <CustomDialog
                open={openModal}
                onCancel={handleCloseModal}
                cancelText="Cerrar"
                title="Contratos"
                maxWidth="sm"
                fullWidth>

                <IconButton onClick={handleCloseModal} sx={{ display: 'block', position: 'absolute', top: '4px', right: '8px' }}>
                    <CloseIcon />
                </IconButton>

                <DataGridGenerico
                    rows={contratosVisita || []}
                    columns={configuratedColumns}
                    hideFooterPagination={true} />

            </CustomDialog>
        </CardBox>);

}